import { type JSONContent, mergeAttributes, Node } from '@tiptap/core';
import { type Node as PMNode, type Schema } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { type EditorView } from '@tiptap/pm/view';

import { cn, tailwindVariants } from '@air/utils-tailwind';

import { useChatComposerStore } from '../../../../store/useChatComposerStore';

/** Shared chip look for composer atoms, `/` suggestion rows, and sent user messages. */
export const skillCommandChip = tailwindVariants({
  base: 'm-0.5 inline-flex h-6 items-center rounded-4 border ads:border-ads-gray-5 border-gray-5 ads:bg-base bg-gray-1 px-1 py-0.5 font-medium font-mono ads:text-primary text-12 text-gray-12 leading-none',
});

/** ProseMirror nodes for a `skillCommand` atom + trailing space. */
export const createSkillCommandInsertNodes = (schema: Schema, slashCommand: string): PMNode[] => {
  const skillCommandNode = schema.nodes.skillCommand?.create({ slashCommand });

  if (!skillCommandNode) {
    return [];
  }

  return [skillCommandNode, schema.text(' ')];
};

/** Doc JSON for a `skillCommand` atom + trailing space. */
export const skillCommandInsertContent = (slashCommand: string): JSONContent[] => [
  {
    attrs: { slashCommand },
    type: 'skillCommand',
  },
  { text: ' ', type: 'text' },
];

const BACKSPACE_KEY = new PluginKey('skillCommandBackspace');

const deleteSkillCommandAt = (view: EditorView, from: number, to: number, slashCommand?: unknown) => {
  if (typeof slashCommand === 'string' && slashCommand) {
    useChatComposerStore.getState().removePendingSkillInstruction(slashCommand);
  }

  view.dispatch(view.state.tr.delete(from, to));
  view.focus();
};

const extendPastTrailingWhitespace = (view: EditorView, from: number) => {
  let to = from;

  while (to < view.state.doc.content.size && /\s/.test(view.state.doc.textBetween(to, to + 1))) {
    to += 1;
  }

  return to;
};

const deleteSkillCommandNode = (view: EditorView, from: number, node: PMNode) =>
  deleteSkillCommandAt(view, from, extendPastTrailingWhitespace(view, from + node.nodeSize), node.attrs.slashCommand);

/** Skill atom immediately before `pos`, skipping any whitespace run ending at `pos`. */
const findPrecedingSkillCommand = (view: EditorView, pos: number): { from: number; slashCommand: unknown } | null => {
  let scanPos = pos;

  while (scanPos > 0 && /\s/.test(view.state.doc.textBetween(scanPos - 1, scanPos))) {
    scanPos -= 1;
  }

  const nodeBefore = view.state.doc.resolve(scanPos).nodeBefore;

  if (nodeBefore?.type.name !== 'skillCommand') {
    return null;
  }

  return {
    from: scanPos - nodeBefore.nodeSize,
    slashCommand: nodeBefore.attrs.slashCommand,
  };
};

/** Deletes the skill chip that currently has DOM focus (Tab/click focus, not text-cursor position). */
const deleteFocusedSkillCommand = (view: EditorView): boolean => {
  const active = document.activeElement;

  if (!(active instanceof HTMLElement) || !view.dom.contains(active)) {
    return false;
  }

  const skillEl = active.closest('[data-skill-command]');

  if (!(skillEl instanceof HTMLElement)) {
    return false;
  }

  let from: number;

  try {
    from = view.posAtDOM(skillEl, 0);
  } catch {
    return false;
  }

  const node = view.state.doc.nodeAt(from);

  if (node?.type.name !== 'skillCommand') {
    return false;
  }

  deleteSkillCommandNode(view, from, node);

  return true;
};

/** Deletes the skill chip (and whitespace back to the cursor) immediately before an empty selection. */
const deleteSkillCommandBeforeCursor = (view: EditorView): boolean => {
  const { selection } = view.state;

  if (!selection.empty) {
    return false;
  }

  const match = findPrecedingSkillCommand(view, selection.from);

  if (!match) {
    return false;
  }

  deleteSkillCommandAt(view, match.from, selection.from, match.slashCommand);

  return true;
};

/**
 * Inline atom for `/skill` chips in the composer — same leaf model as `TokenNode`.
 * `renderHTML` must not include a content hole (`0`).
 */
export const SkillCommandNode = Node.create({
  addAttributes() {
    return {
      slashCommand: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-skill-command'),
        renderHTML: (attributes) => {
          if (!attributes.slashCommand) {
            return {};
          }

          return { 'data-skill-command': attributes.slashCommand };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: BACKSPACE_KEY,
        props: {
          handleKeyDown(view, event) {
            if (event.key !== 'Backspace') {
              return false;
            }

            if (!deleteFocusedSkillCommand(view) && !deleteSkillCommandBeforeCursor(view)) {
              return false;
            }

            event.preventDefault();

            return true;
          },
        },
      }),
    ];
  },

  atom: true,
  group: 'inline',
  inline: true,
  name: 'skillCommand',

  parseHTML() {
    return [{ tag: 'span[data-skill-command]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const slashCommand = typeof node.attrs.slashCommand === 'string' ? node.attrs.slashCommand : '';

    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: cn(skillCommandChip(), 'align-top'),
        tabindex: '0',
      }),
      slashCommand ? `/${slashCommand}` : '',
    ];
  },
});
